import { useApolloClient } from '@apollo/client';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { reportError } from '@frameio/telemetry';
import {
  GetUnopenedNotificationGroupsCount,
  useReportNotificationGroupsLastOpenedMutation,
} from '../gql';
import { modifyCachedNotificationGroupsCount } from '../utils';

type ReportNotificationGroupsLastOpened = {
  onCompleted?: () => void;
};

/**
 * @description `useReportNotificationGroupsLastOpened` - a hook to report the last opened
 * notification id on the client. The the last opened notification id is used on
 * the BE to get the new notification count for a given User on a given Account.
 *
 * @param {Function} onCompleted - callback called when the mutation is completed.
 *
 * @returns {Object}
 * @property {Function} reportNotificationGroupsLastOpenedMutation mutation.
 * @property {boolean} loading - whether the mutation and its associated queries
 * are loading.
 **/
export const useReportNotificationGroupsLastOpened = ({
  onCompleted,
}: ReportNotificationGroupsLastOpened) => {
  const accountId = useActiveAccountId();
  const client = useApolloClient();

  const [reportNotificationGroupsLastOpenedMutation, { error, loading }] =
    useReportNotificationGroupsLastOpenedMutation({
      onCompleted: async () => {
        const data = await client.query({
          query: GetUnopenedNotificationGroupsCount,
          variables: { accountId },
          fetchPolicy: 'no-cache',
        });
        modifyCachedNotificationGroupsCount({
          accountId,
          client,
          timestamp: null,
          totalCount: data?.data?.account?.notificationGroups?.totalCount,
        });

        if (onCompleted) {
          await onCompleted();
        }
      },
      onError: () => {
        reportError(error, {
          message: 'Failed to report notification groups last opened',
        });
      },
      update: () => {
        modifyCachedNotificationGroupsCount({
          accountId,
          client,
          timestamp: null,
          totalCount: 0,
        });
      },
    });

  return {
    loading,
    reportNotificationGroupsLastOpenedMutation,
  };
};
